import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

// Base trigger/input style — matches the rest of the form inputs in
// ConfigPage and SettingsTab (same border, radius, padding).
const TRIGGER_STYLE = {
  width: "100%",
  padding: "10px 13px",
  borderRadius: 8,
  border: "1.5px solid #E2E8F0",
  fontSize: 14,
  color: "#0F172A",
  outline: "none",
  background: "#fff",
  boxSizing: "border-box",
};

// ─── Circular avatar for a Bookings business ───────────────────────────────
// Shows the photo when available, otherwise the first two letters of the
// display name on a soft brand background.
export function BizAvatar({ displayName, email, photoUrl, size = 22 }) {
  const initials = (displayName || email || "?")
    .split(/\s+|[.@_-]/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
      />
    );
  }
  return (
    <div
      aria-hidden="true"
      style={{
        width: size, height: size, borderRadius: "50%",
        background: "#E8F2EF", color: "#004e42",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: Math.max(9, Math.round(size * 0.42)), fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
}

// ─── Searchable combobox with optional per-option leading node ─────────────
// Drop-in replacement for a native <select> when the list is long enough to
// benefit from type-to-filter. The panel is rendered through a React portal
// into document.body so it isn't clipped by ancestor `overflow` rules or
// buried under sibling cards' stacking contexts.
//
// Options: [{ value, label, sublabel?, leadingNode? }]
export function SearchableCombobox({
  value,
  onChange,
  options,
  placeholder = "— Select —",
  disabled = false,
  loading = false,
  clearable = true,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [panelPos, setPanelPos] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const measure = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPanelPos({ left: r.left, top: r.bottom + 4, width: r.width });
    };
    measure();
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    const onDocClick = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value) || null;
  const q = query.trim().toLowerCase();
  // Filter only on the human-readable label. Sublabels (Bookings business
  // emails) share a common domain suffix, which caused every row to match
  // any letter pair appearing in that suffix.
  const filtered = q
    ? options.filter((o) => (o.label || "").toLowerCase().includes(q))
    : options;

  return (
    <div style={{ position: "relative" }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        style={{
          ...TRIGGER_STYLE,
          display: "flex", alignItems: "center", gap: 8,
          cursor: disabled ? "not-allowed" : "pointer",
          textAlign: "left",
          borderColor: open ? "#004e42" : "#E2E8F0",
          opacity: disabled ? 0.6 : 1,
          minHeight: 42,
        }}
      >
        {selected?.leadingNode}
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: selected ? "#0F172A" : "#94A3B8" }}>
          {selected ? selected.label : (loading ? "Loading…" : placeholder)}
        </span>
        {clearable && selected && !disabled && (
          <span
            role="button"
            aria-label="Clear selection"
            onClick={(e) => {
              e.stopPropagation();
              onChange("");
              setQuery("");
              setOpen(false);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 18, height: 18, borderRadius: "50%",
              color: "#94A3B8", cursor: "pointer",
            }}
            title="Clear"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M3.5 3.5l5 5M8.5 3.5l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </span>
        )}
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }}>
          <path d="M3 4.5l3 3 3-3" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && panelPos && createPortal(
        <div
          ref={panelRef}
          style={{
            position: "fixed",
            left: panelPos.left,
            top: panelPos.top,
            width: panelPos.width,
            zIndex: 1000,
            background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,.08)", overflow: "hidden",
          }}
        >
          <div style={{ padding: 8, borderBottom: "1px solid #F1F5F9" }}>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to filter…"
              style={{ ...TRIGGER_STYLE, padding: "8px 11px", fontSize: 13 }}
            />
          </div>
          <div style={{ maxHeight: 260, overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <div style={{ padding: "10px 12px", fontSize: 13, color: "#94A3B8" }}>
                {loading ? "Loading…" : "No matches"}
              </div>
            ) : (
              filtered.map((opt) => {
                const active = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => { onChange(opt.value); setOpen(false); setQuery(""); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, width: "100%",
                      padding: "8px 12px", fontSize: 13, textAlign: "left",
                      background: active ? "#F5FAFA" : "transparent",
                      border: "none", borderBottom: "1px solid #F8FAFC",
                      cursor: "pointer", color: "#0F172A",
                    }}
                  >
                    {opt.leadingNode}
                    <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                      <span style={{ fontWeight: active ? 600 : 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {opt.label}
                      </span>
                      {opt.sublabel && (
                        <span style={{ fontSize: 11, color: "#64748B", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {opt.sublabel}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
